import { Box, Typography } from '@mui/material';
import { V2ThemeProvider } from '@onevision-resources/design-theme';
import { ConversationChannelTypeExtended } from '@provision/chat-utils';
import { buildFriendlyConversationId } from '@provision/formatting-utils';
import { portalPaths } from '@provision/portal-routes';
import { useCallback } from 'react';
import { DynamicIcon, EllipsesMenu, NamedLayoutBlock } from 'src/components';
import { ConversationMessageDraft, useConversationMessageDraft } from 'src/graphql';
import { useNavigateRoute } from 'src/utils/routing';
import DraftList from './components/draft-list';
import { useConversationDraftActions } from './hooks/use-conversation-draft-actions';

export const Drafts = () => {
  const { visibleDrafts } = useConversationMessageDraft();
  const { actionsMenuItems, discardOne } = useConversationDraftActions();
  const { navigate } = useNavigateRoute();

  const handleOpen = useCallback(
    (draft: ConversationMessageDraft) => {
      navigate(
        portalPaths.conversation.full(
          buildFriendlyConversationId(
            draft.partnerId,
            draft.conversation.workspaceConversationId,
          ),
        ),
        {
          state: {
            forceMessageType: draft.isPrivate
              ? ConversationChannelTypeExtended.InternalNote
              : ConversationChannelTypeExtended.Chat,
          },
        },
      );
    },
    [navigate],
  );

  return (
    <NamedLayoutBlock
      title="Recent Drafts"
      rightHeaderAdornment={
        <Box display="flex" alignItems="center" justifyContent="center" gap="2rem">
          <Typography>{visibleDrafts.length} drafts</Typography>
          <V2ThemeProvider>
            <EllipsesMenu options={actionsMenuItems} />
          </V2ThemeProvider>
        </Box>
      }
    >
      {visibleDrafts.length ? (
        <DraftList items={visibleDrafts} onDelete={discardOne} onOpen={handleOpen} />
      ) : (
        <Box
          width="100%"
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
        >
          <DynamicIcon
            name="contract_edit"
            sx={{
              width: '6rem',
              height: '6rem',
              fontSize: '6rem',
              color: (theme) => theme.palette.custom.OV.sys.outlineVariant,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontSize: '1rem',
              color: (theme) => theme.palette.custom.OV.sys.onSurfaceVariant,
            }}
          >
            No drafts — nice!
          </Typography>
        </Box>
      )}
    </NamedLayoutBlock>
  );
};
